/**
 * Componente: WahaHealthAlerts
 * Exibe alertas de inconsistências detectadas no monitoramento WAHA
 * 
 * @version 1.0.0
 * @created 2026-01-05
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import type { WahaAlert } from '@/hooks/useWahaMonitor';

interface WahaHealthAlertsProps {
  alerts: WahaAlert[];
}

/**
 * Componente de alertas de saúde das sessões WAHA
 */
export function WahaHealthAlerts({ alerts }: WahaHealthAlertsProps) {
  if (alerts.length === 0) {
    return (
      <Alert className="border-green-500/50 bg-green-500/10">
        <Info className="h-4 w-4 text-green-500" />
        <AlertTitle className="text-green-500">Tudo certo!</AlertTitle>
        <AlertDescription className="text-muted-foreground">
          Nenhuma inconsistência detectada entre o servidor WAHA e o sistema local.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Alertas de Inconsistência
      </h3>
      
      {alerts.map((alert, index) => {
        // Determinar ícone e estilo baseado no tipo
        let Icon = Info;
        let variant: 'default' | 'destructive' = 'default';
        let borderColor = 'border-blue-500/50';
        let bgColor = 'bg-blue-500/10';
        let textColor = 'text-blue-500';
        
        if (alert.type === 'warning') {
          Icon = AlertTriangle;
          borderColor = 'border-yellow-500/50';
          bgColor = 'bg-yellow-500/10';
          textColor = 'text-yellow-500';
        } else if (alert.type === 'error') {
          Icon = AlertCircle;
          variant = 'destructive';
          borderColor = 'border-destructive/50';
          bgColor = 'bg-destructive/10';
          textColor = 'text-destructive';
        }

        return (
          <Alert 
            key={index} 
            variant={variant}
            className={`${borderColor} ${bgColor}`}
          >
            <Icon className={`h-4 w-4 ${textColor}`} />
            <AlertTitle className={textColor}>
              {alert.count} {alert.count === 1 ? 'problema' : 'problemas'}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {alert.message}
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
